import type { Company, EventDetails } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import sortBy from "lodash/sortBy"
import { useParams } from "next/navigation"

import { Heading } from "@jobteaser/spark/components/Heading"
import { CompanyCard } from "@jobteaser/ui-kit/components/Card/cardComponents/CompanyCard/CompanyCard"

import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"

import bodyStyles from "./Body.module.css"
import styles from "./Companies.module.css"

export const Companies: FunctionComponent = () => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)

  const trackEvent = useTrackEvent()
  const { careerCenter, companies, ownership } = event as EventDetails

  if (typeof companies === "undefined") return null

  if (!event) return null

  if (companies.length === 0) return null

  if (companies.length === 1 && ownership.type === "company" && companies[0].id !== ownership.id) return null

  const handleCompanyCardClick = (company: Company): void => {
    trackEvent("event_ad_logo_click", {
      company_id: company.id,
      company_name: company.name,
      event_id: event.id,
      position: "bottom",
      query_parameters: "",
      service: "event_details",
    })
  }

  const sortedCompanies = sortBy(companies, [
    company => {
      return company.name.toLowerCase()
    },
  ])

  return (
    <div id="companies" className={styles.main} data-testid="event-details-attending-companies">
      <Heading
        as="h2"
        variant="title2"
        className={bodyStyles.title}
        weight="bold"
        data-testid="event-details-attending-companies-title"
      >
        {t("event_details.attending_companies.title")}
      </Heading>

      <ul className={styles.companies__list} data-testid="event-details-attending-companies-list">
        {sortedCompanies.map((company, index) => {
          return (
            <li className={styles.companies__listItem} key={company.id}>
              <CompanyCard
                className={styles.companies__card}
                name={company.name}
                nameTag="h3"
                logoSrc={company.logoUrl}
                description={company.description}
                tagLabel={company.isPartner ? t("event_details.header.partner") : undefined}
                tagLogo={company.isPartner ? careerCenter?.logoUrl : undefined}
                linkProps={{
                  as: "a",
                  href: company.url,
                  onClick: () => handleCompanyCardClick(company),
                }}
                data-testid={`event-details-attending-companies${index}`}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}
